import { useEffect, useRef } from 'react';
import {
  BarController, BarElement, CategoryScale, Chart as ChartJS, Filler, LineController,
  LineElement, LinearScale, PointElement, Tooltip, type ChartConfiguration,
} from 'chart.js';
import { useTheme } from '@/hooks/useTheme';
import { formatBRL, formatBRLCompact } from '@/lib/format';

// Registro explícito: importar só o que é usado mantém o bundle enxuto.
ChartJS.register(
  BarController, BarElement, LineController, LineElement, PointElement,
  CategoryScale, LinearScale, Tooltip, Filler,
);

const probe = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');

/**
 * Cor de um token do tema, normalizada para `#rrggbb`. O Chart.js não entende
 * todas as sintaxes de CSS (a transparência abaixo concatena alfa em hex), e o
 * próprio canvas é quem melhor converte o que o CSS resolveu.
 */
export function tokenColor(token: string): string {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
  if (!probe || !raw) return raw || '#888888';
  probe.fillStyle = '#888888';
  probe.fillStyle = raw;
  return String(probe.fillStyle);
}

interface ChartProps {
  type: 'bar' | 'line';
  labels: string[];
  /** Valores em centavos. */
  data: number[];
  /** Cor da série; padrão: o primeiro token de gráfico do tema. */
  color?: string;
  height?: number;
  label?: string;
  ariaLabel: string;
  /** Linha de referência (ex.: meta do mês), em centavos. */
  target?: number[];
}

/**
 * Gráfico Chart.js controlado por React. A instância é recriada quando os
 * dados ou o tema mudam e destruída no unmount — sem canvas órfão nem
 * vazamento, e sem cor presa ao tema em que o gráfico nasceu.
 */
export function Chart({ type, labels, data, color, height = 220, label = 'Vendido', ariaLabel, target }: ChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { resolved: theme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const series = color ?? tokenColor('--chart-1');
    const muted = tokenColor('--muted-foreground');
    const grid = tokenColor('--border');

    const datasets: ChartConfiguration['data']['datasets'] = [
      type === 'bar'
        ? {
            type: 'bar',
            label,
            data: data.map((cents) => cents / 100),
            backgroundColor: series,
            borderRadius: 4,
            maxBarThickness: 28,
            order: 2,
          }
        : {
            type: 'line',
            label,
            data: data.map((cents) => cents / 100),
            borderColor: series,
            backgroundColor: `${series}24`,
            borderWidth: 2,
            fill: true,
            tension: 0.32,
            pointRadius: data.length > 40 ? 0 : 3,
            pointBackgroundColor: series,
            order: 2,
          },
    ];

    // A meta é referência, não dado: tracejada e neutra, para não competir com a série.
    if (target?.some((value) => value > 0)) {
      datasets.push({
        type: 'line',
        label: 'Meta',
        data: target.map((cents) => cents / 100),
        borderColor: muted,
        borderWidth: 1.5,
        borderDash: [5, 4],
        pointRadius: 0,
        fill: false,
        order: 1,
      });
    }

    const chart = new ChartJS(canvas, {
      type,
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        font: { family: getComputedStyle(document.body).fontFamily },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: tokenColor('--popover'),
            borderColor: grid,
            borderWidth: 1,
            titleColor: tokenColor('--popover-foreground'),
            bodyColor: tokenColor('--popover-foreground'),
            padding: 10,
            callbacks: {
              label: (context) =>
                `${context.dataset.label}: ${formatBRL(Math.round((context.parsed.y ?? 0) * 100))}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: muted,
              font: { size: 11 },
              maxRotation: 0,
              autoSkip: true,
              // Em séries longas, rótulo em cada ponto vira borrão ilegível.
              maxTicksLimit: labels.length > 20 ? 10 : 12,
            },
          },
          y: {
            beginAtZero: true,
            border: { display: false },
            grid: { color: grid },
            ticks: {
              color: muted,
              font: { size: 11 },
              callback: (value) => formatBRLCompact(Math.round(Number(value) * 100)),
            },
          },
        },
      },
    });

    return () => chart.destroy();
  }, [type, labels, data, color, label, target, theme]);

  return (
    <div style={{ height, position: 'relative' }}>
      <canvas ref={canvasRef} role="img" aria-label={ariaLabel} />
    </div>
  );
}
